<template>
  <div class="sm-page sm-page--v2">
    <!-- shared header rail treatment (ops_desk.vue / vsm_board.vue) -->
    <div class="sm-rail">
      <div class="sm-rail-text">
        <h3 class="sm-rail-title">
          Site Map
          <FiFavStar />
        </h3>
        <p class="sm-rail-subtitle">
          Explore everything Fleet Integrations offers for your account.
        </p>
      </div>
    </div>

    <div class="sm-sections-grid">
      <div
        v-for="section in sections"
        :key="section.id"
        class="sm-section-card"
        :class="[
          'sm-section-card--' + section.id,
          { 'sm-section-card--span-2': isWideSection(section.id) },
        ]"
      >
        <div class="sm-section-card-header">
          <div class="sm-section-header-row">
            <div class="sm-section-header-text">
              <h3
                class="sm-section-card-title"
                :class="{ 'sm-section-card-title--dot': section.comingSoon }"
              >
                <!-- count rides the title as a badge; a page count on a
                     not-yet-real feature is noise, so coming soon keeps the
                     plain accent dot instead -->
                <span v-if="!section.comingSoon" class="sm-section-count">{{ section.pages.length }}</span>
                {{ section.title }}
              </h3>
              <p v-if="section.subtitle" class="sm-section-card-sub">{{ section.subtitle }}</p>
            </div>
            <!-- in-flow actions column: the header grows to fit, nothing
                 overlaps the subtitle or clips on the band border. single
                 purpose: the ask is the point of a locked card. -->
            <div class="sm-section-header-actions">
              <!-- horizon/midnight only: classic has no fixed top-nav strip
                   for this to feed (see fi_favorites_bar.vue's own gate) -->
              <label
                v-if="section.id === 'favorites' && horizonNavActive"
                class="sm-fav-navbar-toggle"
              >
                <input type="checkbox" v-model="navFavoritesBarEnabled" />
                Use Toolbar
              </label>
              <span v-if="section.comingSoon" class="sm-section-coming-soon">Coming soon</span>
              <!-- only while still locked: once the feature turns on, the
                   stale localStorage entry must not badge an unlocked card -->
              <span
                v-if="(section.comingSoon || isSectionFullyLocked(section)) && isRequested(sectionFeatureId(section))"
                class="sm-requested-chip"
              >
                <i class="fa fa-check" aria-hidden="true"></i> Requested
              </span>
              <b-button
                v-else-if="section.comingSoon && canRequest"
                size="sm"
                class="sm-section-request-btn sm-request-access-btn"
                @click="openNotifyTicket(section)"
              >
                Notify me
              </b-button>
              <b-button
                v-else-if="!section.comingSoon && isSectionFullyLocked(section) && canRequest && !isSectionRoleLocked(section)"
                size="sm"
                class="sm-section-request-btn sm-request-access-btn"
                @click="openSectionEnableTicket(section)"
              >
                Request access
              </b-button>
              <span
                v-else-if="!section.comingSoon && isSectionFullyLocked(section) && isSectionRoleLocked(section)"
                class="sm-role-note"
              >
                Ask your admin for access
              </span>
            </div>
          </div>
        </div>

        <div class="sm-section-card-body">
          <!-- concise: icon + name pills, no description, no lock/request
               chrome -- every page here is already accessible by definition
               (buildFavoritesSection only includes favoritableDestination
               pages), so none of the grid's locked-tile machinery applies -->
          <div v-if="section.id === 'favorites'" class="sm-fav-chip-row">
            <div
              v-for="page in section.pages"
              :key="page.id"
              class="sm-fav-chip-wrap"
            >
              <component
                :is="pageLinkTag(page)"
                v-bind="pageLinkProps(page)"
                class="sm-fav-chip"
              >
                <i :class="'fa ' + page.icon" class="sm-fav-chip-icon" aria-hidden="true"></i>
                <span class="sm-fav-chip-label">{{ page.name }}</span>
              </component>
              <button
                type="button"
                class="sm-fav-chip-star"
                title="Remove from Favorites"
                :aria-label="'Remove ' + page.name + ' from Favorites'"
                @click.prevent="togglePageFavorite(page)"
              >
                <i class="fa fa-star" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <div
            v-else
            class="sm-page-grid"
            :class="isWideSection(section.id) ? 'sm-page-grid--wide' : 'sm-page-grid--section'"
          >
            <div
              v-for="page in section.pages"
              :key="page.id"
              class="sm-page-wrap"
              :class="{ 'sm-page-wrap--locked': !page.accessible }"
            >
              <component
                v-if="page.accessible"
                :is="pageLinkTag(page)"
                v-bind="pageLinkProps(page)"
                class="sm-page-tile"
              >
                <div class="sm-page-tile-head">
                  <i :class="'fa ' + page.icon" class="sm-page-tile-glyph" aria-hidden="true"></i>
                  <div class="sm-page-tile-title">{{ page.name }}</div>
                </div>
                <div class="sm-page-tile-desc">{{ page.description }}</div>
              </component>

              <div
                v-else
                class="sm-page-tile sm-page-tile--locked"
              >
                <!-- the page's real icon at reduced saturation, inline with
                     the title; the lock sits quietly at the end of the row -->
                <div class="sm-page-tile-head">
                  <i :class="'fa ' + (page.comingSoon ? 'fa-clock-o' : page.icon)" class="sm-page-tile-glyph sm-page-tile-glyph--locked" aria-hidden="true"></i>
                  <div class="sm-page-tile-title">{{ page.name }}</div>
                  <i v-if="!page.comingSoon" class="fa fa-lock sm-page-tile-lock" aria-hidden="true"></i>
                </div>
                <div class="sm-page-tile-desc">{{ page.description }}</div>
                <!-- per-tile coming-soon label only when the section header
                     is not already saying it for the whole card -->
                <span
                  v-if="page.comingSoon"
                  v-show="!section.comingSoon"
                  class="sm-page-coming-soon"
                >
                  Coming soon
                </span>
                <!-- in a fully role-locked section the header note already
                     says it once; repeating it on every tile is noise -->
                <span
                  v-else-if="page.lockType === 'role'"
                  v-show="!isSectionFullyLocked(section)"
                  class="sm-role-note"
                >
                  Ask your admin for access
                </span>
                <!-- one footer element, never a stack: the requested chip, the
                     request button (reason in its tooltip), or the reason as
                     plain text. fully locked sections say it once in the
                     section header instead of on every tile. -->
                <template v-else-if="!isSectionFullyLocked(section)">
                  <span v-if="isRequested(page.id)" class="sm-requested-chip sm-requested-chip--tile">
                    <i class="fa fa-check" aria-hidden="true"></i> Requested
                  </span>
                  <b-button
                    v-else-if="canRequest"
                    size="sm"
                    class="sm-request-btn sm-request-access-btn"
                    :title="page.lockedReason"
                    @click="openEnableTicket(page, section)"
                  >
                    Request access
                  </b-button>
                  <span v-else-if="page.lockedReason" class="sm-tile-reason">{{ page.lockedReason }}</span>
                </template>
              </div>

              <!-- sibling overlay, not nested in the tile link: the wrap's
                   position:relative anchors it to the tile's upper-right
                   corner, and a plain click.prevent needs no stopPropagation
                   games with the router-link underneath (report_dashboard_shell.vue's
                   .rds-star uses the same layout for the same reason) -->
              <button
                v-if="canFavoritePage(page)"
                type="button"
                class="sm-page-fav-star"
                :class="{ 'sm-page-fav-star--on': isPageFavorite(page) }"
                :title="isPageFavorite(page) ? 'Remove from Favorites' : 'Add to Favorites'"
                :aria-pressed="isPageFavorite(page) ? 'true' : 'false'"
                :aria-label="(isPageFavorite(page) ? 'Remove ' : 'Add ') + page.name + (isPageFavorite(page) ? ' from' : ' to') + ' Favorites'"
                @click.prevent="togglePageFavorite(page)"
              >
                <i class="fa" :class="isPageFavorite(page) ? 'fa-star' : 'fa-star-o'" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { buildSiteMapSections, canRequestFeatures, buildFavoritesSection, favoritableDestination } from '@/utils/site_map_pages'
import { favState, favKey, isFavorite, toggleFavorite, ensureFavoritesLoaded } from '@/nav/nav_favorites'
import { setNavFavoritesBarEnabled } from '@/nav/nav_favorites_bar'
import horizonNav from '@/nav/horizon_nav_mixin'

// support_ticket_create.vue's categoryOptions values, mapped per section so
// the prefilled ticket lands in the right support queue (the old literal
// 'feature' was a priority value, not a category, and left the select blank)
const SECTION_TICKET_CATEGORY = {
  core: 'general',
  maps: 'maps',
  bookings: 'actuals',
  alerts: 'alerts',
  vsm: 'vsm',
  dispatch: 'general',
  ops: 'general',
  reports: 'reports',
}

// written by support_ticket_create.vue on successful submit of a
// site-map-sourced ticket; read here to show the "Requested" chip
const REQUESTED_LS_KEY = 'sm_requested_features'

function readRequestedIds() {
  try {
    const list = JSON.parse(localStorage.getItem(REQUESTED_LS_KEY))
    return Array.isArray(list) ? list : []
  } catch (e) {
    return []
  }
}

export default {
  name: 'site_map',
  mixins: [horizonNav],
  data() {
    return {
      requestedIds: readRequestedIds(),
      // held in data (not just imported) so the template re-renders when
      // the shared favorites store changes -- same trick as fi_fav_star.vue
      favState,
    }
  },
  mounted() {
    ensureFavoritesLoaded()
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    baseSections() {
      return buildSiteMapSections(this.user, this.viewAs)
    },
    favoritesSection() {
      return buildFavoritesSection(this.baseSections, this.favState.keys, favKey)
    },
    sections() {
      return this.favoritesSection ? [this.favoritesSection, ...this.baseSections] : this.baseSections
    },
    canRequest() {
      return canRequestFeatures(this.user, this.viewAs)
    },
    // "Use Toolbar" checkbox on the Favorites section header. mirrors the
    // same profile/nav_favorites_bar state fi_favorites_bar.vue reads, so
    // the strip appears/disappears live as this is toggled.
    navFavoritesBarEnabled: {
      get() {
        return !!this.$store.state.profile.nav_favorites_bar
      },
      set(value) {
        setNavFavoritesBarEnabled(value)
      },
    },
  },
  methods: {
    isWideSection(sectionId) {
      return sectionId === 'favorites' || sectionId === 'core' || sectionId === 'reports' || sectionId === 'dispatch'
    },
    canFavoritePage(page) {
      return !!favoritableDestination(page)
    },
    isPageFavorite(page) {
      const dest = favoritableDestination(page)
      // touch favState.keys so vue tracks the dependency through isFavorite
      return !!dest && this.favState.keys.length >= 0 && isFavorite(dest)
    },
    togglePageFavorite(page) {
      const dest = favoritableDestination(page)
      if (dest) toggleFavorite(dest)
    },
    isSectionFullyLocked(section) {
      return section.pages.length > 0 && section.pages.every(p => !p.accessible)
    },
    sectionLockedReason(section) {
      const locked = section.pages.find(p => !p.accessible)
      return (locked && locked.lockedReason) || 'Not enabled for your account'
    },
    // a fully-locked section where every lock is a role lock has nothing to
    // sell: the tenant already has it, this user just isn't allowed in
    isSectionRoleLocked(section) {
      const locked = section.pages.filter(p => !p.accessible)
      return locked.length > 0 && locked.every(p => p.lockType === 'role')
    },
    sectionFeatureId(section) {
      return 'section:' + section.id
    },
    ticketCategoryFor(page, section) {
      if (page && page.route === 'fleet_assistant') return 'fleet-assistant'
      return SECTION_TICKET_CATEGORY[section.id] || 'general'
    },
    isRequested(featureId) {
      return this.requestedIds.includes(featureId)
    },
    pageLinkTag(page) {
      return page.external ? 'a' : 'router-link'
    },
    pageLinkProps(page) {
      if (page.external) {
        return { href: page.path, target: '_blank', rel: 'noopener' }
      }
      if (page.path) {
        return { to: { path: page.path } }
      }
      return { to: { name: page.route, query: page.query } }
    },
    openEnableTicket(page, section) {
      const subject = `Enable: ${page.name}`
      const description = [
        `I'd like access to "${page.name}" in Fleet Integrations.`,
        page.lockedReason ? `\nReason shown: ${page.lockedReason}` : '',
        page.description ? `\n\nPage description: ${page.description}` : '',
      ].join('')
      this.$router.push({
        name: 'support_ticket_create',
        query: {
          category: this.ticketCategoryFor(page, section),
          subject,
          description,
          source: 'site_map',
          feature_id: page.id,
        },
      })
    },
    openSectionEnableTicket(section) {
      if (section.id === 'reports') {
        this.$router.push({
          name: 'support_ticket_create',
          query: {
            category: 'reports',
            subject: "I'd like to sign up for the report suite",
            source: 'site_map',
            feature_id: this.sectionFeatureId(section),
          },
        })
        return
      }
      const lockedReason = this.sectionLockedReason(section)
      const subject = `Enable: ${section.title}`
      const description = [
        `I'd like access to ${section.title} in Fleet Integrations.`,
        lockedReason ? `\nReason shown: ${lockedReason}` : '',
        section.subtitle ? `\n\n${section.subtitle}` : '',
      ].join('')
      this.$router.push({
        name: 'support_ticket_create',
        query: {
          category: this.ticketCategoryFor(null, section),
          subject,
          description,
          source: 'site_map',
          feature_id: this.sectionFeatureId(section),
        },
      })
    },
    // coming-soon sections capture interest instead of discarding it
    openNotifyTicket(section) {
      this.$router.push({
        name: 'support_ticket_create',
        query: {
          category: this.ticketCategoryFor(null, section),
          subject: `Interested in: ${section.title}`,
          description: `I'd like to be notified when ${section.title} becomes available in Fleet Integrations.`,
          source: 'site_map',
          feature_id: this.sectionFeatureId(section),
        },
      })
    },
  },
}
</script>

<style scoped>
.sm-page--v2 {
  padding: 0;
  margin: 16px 18px 24px;
  background: transparent;
  border-radius: 0;
}

/* horizon/midnight wrap routed pages in .fi-hz-content, which already pays
   the page gutter -- classic has no shell, so the base rule above pays it */
.fi-hz-content .sm-page--v2 {
  margin: 0 0 24px;
}

/* shared header rail: same recipe as ops_desk.vue's .od-rail / vsm_board's
   .vb-rail (steelblue accent edge, square left, soft right) */
.sm-rail {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--fi-ops-rail-bg);
  border-left: 3px solid var(--fi-rail-accent);
  border-radius: 0 6px 6px 0;
  padding: 12px 16px;
  margin-bottom: 14px;
  text-align: left;
}

.sm-rail-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 32px;
  min-width: 0;
  flex: 1 1 auto;
}

.sm-rail-title {
  margin: 0;
  padding: 0;
  font-size: 22px;
  line-height: 1.2;
  font-weight: 700;
  color: #1d1d1f;
  letter-spacing: -0.1px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.sm-rail-subtitle {
  margin: 1px 0 0;
  font-size: 12.5px;
  line-height: 1.4;
  color: var(--fi-ops-eyebrow);
}

.sm-sections-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px;
  padding: 0;
}

@media (max-width: 900px) {
  .sm-sections-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

.sm-section-card {
  border: 2px solid color-mix(in srgb, var(--sm-section-accent) 28%, #d8dee6);
  border-radius: 16px;
  background: var(--sm-section-accent-soft);
  box-shadow:
    0 4px 22px color-mix(in srgb, var(--sm-section-accent) 14%, transparent),
    0 1px 4px rgba(20, 40, 60, 0.06);
  display: flex;
  flex-direction: column;
  min-width: 0;
  position: relative;
  overflow: hidden;
  --sm-section-accent: #4682b4;
  --sm-section-accent-soft: #dceaf8;
  --sm-section-count-bg: var(--sm-section-accent);
  --sm-section-count-color: #fff;
}

.sm-section-card::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 6px;
  background: linear-gradient(
    90deg,
    var(--sm-section-accent),
    color-mix(in srgb, var(--sm-section-accent) 55%, white)
  );
  z-index: 3;
}

.sm-section-card::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  background: linear-gradient(
    180deg,
    var(--sm-section-accent),
    color-mix(in srgb, var(--sm-section-accent) 70%, white)
  );
  z-index: 1;
}

.sm-section-card--favorites {
  --sm-section-accent: #d39a2e;
  --sm-section-accent-soft: #faf0da;
}

.sm-section-card--core {
  --sm-section-accent: #3a72a8;
  --sm-section-accent-soft: #dceaf8;
}

.sm-section-card--maps {
  --sm-section-accent: #2d8a52;
  --sm-section-accent-soft: #d4eedf;
}

.sm-section-card--bookings {
  --sm-section-accent: #6a4fa8;
  --sm-section-accent-soft: #e4d8f5;
}

.sm-section-card--alerts {
  --sm-section-accent: #c86e18;
  --sm-section-accent-soft: #fde8d0;
}

.sm-section-card--vsm {
  --sm-section-accent: #4a6878;
  --sm-section-accent-soft: #dce4ed;
}

.sm-section-card--dispatch {
  --sm-section-accent: #4a52a0;
  --sm-section-accent-soft: #dadef5;
}

.sm-section-card--ops {
  --sm-section-accent: #6a5290;
  --sm-section-accent-soft: #e2d8f0;
}

.sm-section-card--reports {
  --sm-section-accent: #2a7888;
  --sm-section-accent-soft: #cce9ee;
}

.sm-section-card--span-2 {
  grid-column: 1 / -1;
}

.sm-section-card-header {
  padding: 16px 16px 14px 24px;
  background: color-mix(in srgb, var(--sm-section-accent-soft) 55%, #fff);
  border-bottom: 2px solid color-mix(in srgb, var(--sm-section-accent) 22%, #eef1f5);
  position: relative;
  z-index: 2;
}

.sm-section-header-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
}

.sm-section-header-text {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.sm-section-header-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
  padding-top: 2px;
}

/* far right of the Favorites header, above whatever else lands in the
   actions column (never both at once in practice: this section is neither
   comingSoon nor lockable) */
.sm-fav-navbar-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: color-mix(in srgb, var(--sm-section-accent) 75%, #1a2533);
  cursor: pointer;
  white-space: nowrap;
  margin: 0;
}

.sm-fav-navbar-toggle input[type="checkbox"] {
  width: 14px;
  height: 14px;
  margin: 0;
  accent-color: var(--fi-chrome-star);
  cursor: pointer;
}

/* badge in the title line, sized to ride the 18px title without stretching it */
.sm-section-count {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 6px;
  background: var(--sm-section-count-bg);
  color: var(--sm-section-count-color);
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.02em;
  vertical-align: middle;
  position: relative;
  top: -1px;
  margin-right: 7px;
  box-shadow: 0 1px 3px color-mix(in srgb, var(--sm-section-accent) 40%, transparent);
}

.sm-section-card-title {
  font-size: 18px;
  font-weight: 700;
  color: color-mix(in srgb, var(--sm-section-accent) 72%, #1a2533);
  margin: 0;
  line-height: 1.25;
  text-align: left;
  letter-spacing: -0.01em;
}

/* decorative dot only where the count badge is hidden (coming soon) */
.sm-section-card-title--dot::before {
  content: '';
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: var(--sm-section-accent);
  margin-right: 9px;
  vertical-align: middle;
  position: relative;
  top: -1px;
  box-shadow: 0 1px 3px color-mix(in srgb, var(--sm-section-accent) 40%, transparent);
}

.sm-section-card-sub {
  color: #4a5568;
  font-size: 13px;
  margin: 4px 0 0;
  line-height: 1.5;
  text-align: left;
}

.sm-section-request-btn {
  white-space: nowrap;
  flex-shrink: 0;
}

.sm-section-coming-soon {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6a5290;
  background: #f0eaf8;
  border: 1px solid color-mix(in srgb, var(--sm-section-accent) 35%, #d8dee6);
  border-radius: 999px;
  padding: 4px 10px;
  white-space: nowrap;
}

.sm-page-coming-soon {
  margin-top: 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6a5290;
}

/* brand-azure action family (tokens.css doctrine: azure = actions, green =
   lifecycle status). green now belongs to the post-submit Requested chip. */
.sm-request-access-btn {
  font-weight: 600;
  border-color: var(--fi-brand-tint-border) !important;
  color: var(--fi-brand-strong) !important;
  background: var(--fi-brand-tint) !important;
  line-height: 1.25;
  border-radius: 999px !important;
}

.sm-section-request-btn.sm-request-access-btn {
  font-size: 12px !important;
  padding: 4px 12px !important;
  border-width: 1.5px !important;
}

.sm-request-btn.sm-request-access-btn {
  font-size: 11px !important;
  padding: 3px 11px !important;
  border-width: 1px !important;
  margin-top: 8px;
}

.sm-request-access-btn:hover,
.sm-request-access-btn:focus {
  background: var(--fi-brand-strong) !important;
  border-color: var(--fi-brand-strong) !important;
  color: #fff !important;
}

.sm-requested-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  color: var(--fi-success);
  background: var(--fi-success-tint);
  border: 1px solid color-mix(in srgb, var(--fi-success) 30%, transparent);
  border-radius: 999px;
  padding: 4px 11px;
  white-space: nowrap;
}

.sm-requested-chip--tile {
  margin-top: 8px;
  font-size: 10px;
  padding: 3px 9px;
}

/* when the tile has no button to show, the reason reads as quiet plain
   text, not another pill */
.sm-tile-reason {
  margin-top: 8px;
  font-size: 11px;
  color: #6b7a89;
  line-height: 1.35;
}

/* role locks have nothing to sell: quiet note instead of a buy button */
.sm-role-note {
  margin-top: 8px;
  font-size: 11px;
  font-style: italic;
  color: #6b7a89;
  white-space: nowrap;
}

.sm-section-card-body {
  padding: 16px 16px 18px 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  background: color-mix(in srgb, var(--sm-section-accent-soft) 38%, #fff);
  position: relative;
  z-index: 2;
}

.sm-page-grid {
  display: grid;
  gap: 12px;
  width: 100%;
}

.sm-page-grid--section,
.sm-page-grid--wide {
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
}

@media (max-width: 900px) {
  .sm-page-grid--section,
  .sm-page-grid--wide {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  }
}

@media (max-width: 520px) {
  .sm-page-grid--section,
  .sm-page-grid--wide {
    grid-template-columns: 1fr;
  }
}

.sm-page-wrap {
  position: relative;
  min-width: 0;
}

/* tile-scale version of the dashboard widget card language (main_dashboard
   .mdash-card): accent top bar, tinted body, accent-mixed border and title */
.sm-page-tile {
  border: 1px solid color-mix(in srgb, var(--sm-section-accent, #4682b4) 30%, #e8ecf0);
  border-radius: 12px;
  /* extra right padding reserves room for the .sm-page-fav-star overlay so
     a long title never runs under it (report_dashboard_shell.vue's .rds-card
     reserves the same way for its own star) */
  padding: 15px 34px 12px 14px;
  min-height: 104px;
  height: 100%;
  background: color-mix(in srgb, var(--sm-section-accent, #4682b4) 6%, #fff);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  box-shadow:
    0 2px 10px color-mix(in srgb, var(--sm-section-accent, #4682b4) 10%, transparent),
    0 1px 3px rgba(20, 40, 60, 0.04);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  cursor: pointer;
  text-decoration: none !important;
  position: relative;
  overflow: hidden;
}

.sm-page-tile::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 4px;
  background: linear-gradient(
    90deg,
    var(--sm-section-accent, #4682b4),
    color-mix(in srgb, var(--sm-section-accent, #4682b4) 55%, white)
  );
}

.sm-page-tile:hover {
  border-color: color-mix(in srgb, var(--sm-section-accent, #4682b4) 50%, #e8ecf0);
  box-shadow:
    0 6px 18px color-mix(in srgb, var(--sm-section-accent, #4682b4) 18%, transparent),
    0 1px 4px rgba(20, 40, 60, 0.06);
  transform: translateY(-2px);
}

/* locked stays deliberately gray next to its colorful siblings */
.sm-page-tile--locked {
  background: #f6f7f9;
  border-color: color-mix(in srgb, var(--sm-section-accent, #4682b4) 14%, #e4e8ed);
  border-style: dashed;
  cursor: default;
  box-shadow: 0 1px 3px rgba(20, 40, 60, 0.04);
}

.sm-page-tile--locked::before {
  display: none;
}

.sm-page-tile--locked:hover {
  border-color: color-mix(in srgb, var(--sm-section-accent, #4682b4) 14%, #e4e8ed);
  background: #f6f7f9;
  box-shadow: 0 1px 3px rgba(20, 40, 60, 0.04);
  transform: none;
}

/* linkedin-card layout: bare accent glyph on the same line as the title,
   no icon box, everything left-aligned */
/* top-justified: glyph and lock ride the title's FIRST line even when the
   title wraps; the line-heights below pin them to that 18px line box */
.sm-page-tile-head {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  width: 100%;
  margin-bottom: 6px;
}

.sm-page-tile-glyph {
  font-size: 16px;
  width: 20px;
  line-height: 18px;
  text-align: center;
  color: var(--sm-section-accent, #4682b4);
  flex-shrink: 0;
}

/* locked keeps the page's identity at reduced saturation */
.sm-page-tile-glyph--locked {
  color: color-mix(in srgb, var(--sm-section-accent) 45%, #9aa7b4);
}

.sm-page-tile-lock {
  margin-left: auto;
  padding-left: 6px;
  font-size: 10px;
  line-height: 18px;
  color: #93a1b0;
  flex-shrink: 0;
}

/* sibling overlay pinned to the tile's upper-right corner (.sm-page-wrap
   carries position:relative already); sits above the tile so it never
   competes with the router-link/anchor underneath for the click */
.sm-page-fav-star {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: var(--fi-radius-sm);
  background: transparent;
  color: var(--fi-text-muted) !important;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  opacity: 0.55;
  transition: color var(--fi-motion-quick), opacity var(--fi-motion-quick), background-color var(--fi-motion-quick);
}

.sm-page-fav-star:hover {
  background: var(--fi-chrome-hover-bg);
  color: var(--fi-chrome-star) !important;
  opacity: 1;
}

.sm-page-fav-star--on,
.sm-page-fav-star--on:hover {
  color: var(--fi-chrome-star) !important;
  opacity: 1;
}

.sm-page-fav-star:focus { outline: none; box-shadow: none; }
.sm-page-fav-star:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--fi-action-primary-ring);
}

/* ---- favorites section: concise icon+name pills, no description card ---- */
.sm-fav-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.sm-fav-chip-wrap { position: relative; }

.sm-fav-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  padding: 0 30px 0 12px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid color-mix(in srgb, var(--sm-section-accent) 30%, #e8ecf0);
  color: color-mix(in srgb, var(--sm-section-accent) 60%, #1a2533);
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  text-decoration: none !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.sm-fav-chip:hover {
  border-color: color-mix(in srgb, var(--sm-section-accent) 55%, #e8ecf0);
  box-shadow: 0 2px 8px color-mix(in srgb, var(--sm-section-accent) 16%, transparent);
  transform: translateY(-1px);
}

.sm-fav-chip-icon {
  font-size: 12px;
  color: var(--sm-section-accent);
}

/* hover-reveal unstar, vertically centered on the pill (not a corner --
   there's no "upper right" on a single-line chip) -- same recipe as
   fi_section_nav.vue's .fi-sn-fav-drop and fi_favorites_bar.vue's own chip */
.sm-fav-chip-star {
  position: absolute;
  top: 50%;
  right: 5px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: var(--fi-radius-sm);
  background: transparent;
  color: var(--fi-chrome-star) !important;
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  opacity: 0;
  transition: opacity var(--fi-motion-quick), background-color var(--fi-motion-quick);
}
.sm-fav-chip-wrap:hover .sm-fav-chip-star,
.sm-fav-chip-star:focus-visible { opacity: 1; }
.sm-fav-chip-star:hover { background: var(--fi-chrome-hover-bg); }
@media (hover: none) {
  .sm-fav-chip-star { opacity: 0.75; }
}

.sm-page-tile-title {
  font-size: 14px;
  font-weight: 700;
  color: color-mix(in srgb, var(--sm-section-accent, #4682b4) 55%, #1a2533);
  padding: 0;
  line-height: 1.3;
  min-width: 0;
  text-align: left;
}

/* locked titles stay neutral next to the accent-inked live tiles */
.sm-page-tile--locked .sm-page-tile-title {
  color: #47535f;
}

.sm-page-tile-desc {
  font-size: 12px;
  color: #434a54;
  flex: 1;
  width: 100%;
  line-height: 1.5;
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding: 0;
}

/* end-of-stylesheet on purpose: media queries add no specificity, so this
   must come after the base .sm-page--v2 rule to win */
@media (max-width: 600px) {
  .sm-page--v2 {
    margin: 12px 10px 16px;
  }

  .fi-hz-content .sm-page--v2 {
    margin: 0 0 16px;
  }

  .sm-rail {
    padding: 10px 12px;
    gap: 10px;
  }

  .sm-rail-title {
    font-size: 19px;
  }
}
</style>
